import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import {
  clampPoint,
  createRoute,
  createShape,
  createSymbol,
  createText,
  defaultSymbolSize,
  defaultTextSize,
  layerDefaultFill,
  layerOrder,
  pointsToAttr,
  isDuplicateLabelText,
  isRouteVisible,
  pointInPolygon,
  ROUTE_COLOR,
  resolvedShapeFill,
  roundPoint,
  shapeCentroid,
  shapeDisplayLabel,
  shapeLabelFontSize,
  shapeLabelTextLength,
  translatePoints,
  type PlanLayer,
  type PlanPoint,
  type PlanRoute,
  type PlanShape,
  type PlanSymbol,
  type PlanText,
  type SitePlanVectorDocument,
} from "@/lib/sitePlanDocument";
import { getSymbolDefinition, SitePlanSymbolDefs, SYMBOL_BOX, symbolDomId } from "@/lib/sitePlanSymbols";

export type EditorTool =
  | { kind: "select" }
  | { kind: "pan" }
  | { kind: "polygon" }
  | { kind: "rect" }
  | { kind: "symbol"; type: string }
  | { kind: "text" }
  | { kind: "route"; pitchId: string | null };

export type EditorSelection =
  | { kind: "shape"; id: string }
  | { kind: "symbol"; id: string }
  | { kind: "text"; id: string }
  | { kind: "route"; id: string };

export interface SitePlanVectorEditorHandle {
  fitToView: () => void;
  zoomBy: (factor: number) => void;
}

interface SitePlanVectorEditorProps {
  document: SitePlanVectorDocument;
  /** `commit=true` am Ende einer Geste → Eintrag in der Undo-Historie. */
  onChange: (next: SitePlanVectorDocument, commit: boolean) => void;
  tool: EditorTool;
  onToolChange: (tool: EditorTool) => void;
  selection: EditorSelection | null;
  onSelectionChange: (selection: EditorSelection | null) => void;
  pitchCodes: ReadonlyMap<string, string>;
  /** Farbe der Platzkategorie je Stellplatz-ID. */
  pitchCategoryColors?: ReadonlyMap<string, string | null>;
  sourceImage: string | null;
  showSourceImage: boolean;
  sourceImageOpacity: number;
  showLabels: boolean;
  readOnly: boolean;
  /** Ebene für neu gezeichnete Flächen. */
  newShapeLayer: PlanLayer;
  /** Platz, dessen Anfahrtslinien gerade eingeblendet sind. */
  revealedPitchId?: string | null;
  /** Nach dem Zeichnen einer Anfahrt, damit die neue Linie sichtbar bleibt. */
  onRouteFinished?: (route: PlanRoute) => void;
  /** Zugriff auf Ansicht-Funktionen (Toolbar der Seite). */
  handleRef?: React.MutableRefObject<SitePlanVectorEditorHandle | null>;
  className?: string;
}

interface View {
  /** Pixel pro Plan-Einheit */
  scale: number;
  /** Sichtbarer Ursprung in Plan-Einheiten */
  x: number;
  y: number;
}

type Gesture =
  | { kind: "pan"; startClient: PlanPoint; startView: View }
  | {
      kind: "move-shape";
      id: string;
      start: PlanPoint;
      original: PlanPoint[];
      moved: boolean;
      /** Loslassen ohne Ziehen wählt das nächste Objekt unter dem Cursor. */
      cycleOnClick: boolean;
      hitPoint: PlanPoint;
    }
  | {
      kind: "move-symbol";
      id: string;
      start: PlanPoint;
      originalX: number;
      originalY: number;
      moved: boolean;
      cycleOnClick: boolean;
      hitPoint: PlanPoint;
    }
  | {
      kind: "move-text";
      id: string;
      start: PlanPoint;
      originalX: number;
      originalY: number;
      moved: boolean;
      cycleOnClick: boolean;
      hitPoint: PlanPoint;
    }
  | { kind: "vertex"; id: string; index: number; moved: boolean }
  | {
      kind: "move-route";
      id: string;
      start: PlanPoint;
      original: PlanPoint[];
      moved: boolean;
      cycleOnClick: boolean;
      hitPoint: PlanPoint;
    }
  | { kind: "route-vertex"; id: string; index: number; moved: boolean }
  | { kind: "rect-draw"; start: PlanPoint; current: PlanPoint }
  | { kind: "resize-symbol"; id: string; center: PlanPoint; moved: boolean };

function sameHit(a: EditorSelection | null, b: EditorSelection): boolean {
  return a !== null && a.kind === b.kind && a.id === b.id;
}

function pointInSymbol(symbol: PlanSymbol, point: PlanPoint): boolean {
  const rad = (-symbol.rotation * Math.PI) / 180;
  const dx = point.x - symbol.x;
  const dy = point.y - symbol.y;
  const lx = dx * Math.cos(rad) - dy * Math.sin(rad);
  const ly = dx * Math.sin(rad) + dy * Math.cos(rad);
  return Math.abs(lx) <= symbol.size / 2 && Math.abs(ly) <= symbol.size / 2;
}

function pointInText(text: PlanText, point: PlanPoint): boolean {
  const rad = (-text.rotation * Math.PI) / 180;
  const dx = point.x - text.x;
  const dy = point.y - text.y;
  const lx = dx * Math.cos(rad) - dy * Math.sin(rad);
  const ly = dx * Math.sin(rad) + dy * Math.cos(rad);
  const halfW = Math.max(text.size * 0.5, (text.size * 0.55 * Math.max(1, text.text.length)) / 2);
  const halfH = text.size * 0.65;
  return Math.abs(lx) <= halfW && Math.abs(ly) <= halfH;
}

function distanceToSegment(point: PlanPoint, a: PlanPoint, b: PlanPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(point.x - a.x, point.y - a.y);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / len2));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}

function polylineHit(points: PlanPoint[], point: PlanPoint, slop: number): boolean {
  for (let i = 1; i < points.length; i += 1) {
    if (distanceToSegment(point, points[i - 1]!, points[i]!) <= slop) return true;
  }
  return false;
}

/** Oberstes Objekt zuerst – unabhängig davon, welches Element den Klick abfängt. */
function stackAt(
  doc: SitePlanVectorDocument,
  point: PlanPoint,
  pitchCodes: ReadonlyMap<string, string>,
  revealedPitchId: string | null,
  slop: number,
): EditorSelection[] {
  const hits: EditorSelection[] = [];
  const routes = doc.routes ?? [];
  for (let i = routes.length - 1; i >= 0; i -= 1) {
    const route = routes[i]!;
    if (!isRouteVisible(route, revealedPitchId)) continue;
    if (polylineHit(route.points, point, slop)) hits.push({ kind: "route", id: route.id });
  }
  for (let i = doc.texts.length - 1; i >= 0; i -= 1) {
    const text = doc.texts[i]!;
    if (isDuplicateLabelText(text, doc, pitchCodes)) continue;
    if (pointInText(text, point)) hits.push({ kind: "text", id: text.id });
  }
  for (let i = doc.symbols.length - 1; i >= 0; i -= 1) {
    const symbol = doc.symbols[i]!;
    if (pointInSymbol(symbol, point)) hits.push({ kind: "symbol", id: symbol.id });
  }
  const shapes = doc.shapes.map((shape, index) => ({ shape, index }));
  shapes.sort((a, b) => {
    const layer = layerOrder(b.shape.layer) - layerOrder(a.shape.layer);
    return layer !== 0 ? layer : b.index - a.index;
  });
  for (const { shape } of shapes) {
    if (pointInPolygon(point, shape.points)) hits.push({ kind: "shape", id: shape.id });
  }
  return hits;
}

function nextInStack(hits: EditorSelection[], current: EditorSelection | null): EditorSelection | null {
  if (hits.length === 0) return null;
  const index = hits.findIndex((hit) => sameHit(current, hit));
  if (index === -1) return hits[1] ?? hits[0]!;
  return hits[(index + 1) % hits.length]!;
}

const MIN_SCALE_FACTOR = 0.2;
const MAX_SCALE_FACTOR = 40;
const HANDLE_PX = 7;
const CLOSE_RADIUS_PX = 12;

function cursorForTool(tool: EditorTool, readOnly: boolean): string {
  if (readOnly) return "default";
  switch (tool.kind) {
    case "pan":
      return "grab";
    case "polygon":
    case "rect":
    case "symbol":
    case "text":
    case "route":
      return "crosshair";
    default:
      return "default";
  }
}

export function SitePlanVectorEditor({
  document: doc,
  onChange,
  tool,
  onToolChange,
  selection,
  onSelectionChange,
  pitchCodes,
  pitchCategoryColors,
  sourceImage,
  showSourceImage,
  sourceImageOpacity,
  showLabels,
  readOnly,
  newShapeLayer,
  revealedPitchId = null,
  onRouteFinished,
  handleRef,
  className,
}: SitePlanVectorEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const docRef = useRef(doc);
  docRef.current = doc;
  const gestureRef = useRef<Gesture | null>(null);
  const [draftPoints, setDraftPoints] = useState<PlanPoint[]>([]);
  const [cursorPoint, setCursorPoint] = useState<PlanPoint | null>(null);
  const [rectDraft, setRectDraft] = useState<{ start: PlanPoint; current: PlanPoint } | null>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fittedForRef = useRef<string>("");

  const canEdit = !readOnly;

  // --- Container-Größe beobachten -------------------------------------------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fitScale = useMemo(() => {
    if (containerSize.width <= 0 || containerSize.height <= 0) return 1;
    return Math.min(containerSize.width / doc.width, containerSize.height / doc.height) * 0.96;
  }, [containerSize.width, containerSize.height, doc.width, doc.height]);

  const fitToView = useCallback(() => {
    if (containerSize.width <= 0 || containerSize.height <= 0) return;
    const scale = fitScale;
    setView({
      scale,
      x: (doc.width - containerSize.width / scale) / 2,
      y: (doc.height - containerSize.height / scale) / 2,
    });
  }, [containerSize.width, containerSize.height, doc.width, doc.height, fitScale]);

  // Beim ersten Layout und bei neuer Plangröße einpassen.
  useEffect(() => {
    const key = `${doc.width}x${doc.height}`;
    if (containerSize.width > 0 && fittedForRef.current !== key) {
      fittedForRef.current = key;
      fitToView();
    }
  }, [containerSize.width, doc.width, doc.height, fitToView]);

  useEffect(() => {
    setImageError(false);
  }, [sourceImage]);

  const zoomAt = useCallback(
    (factor: number, clientX?: number, clientY?: number) => {
      const svg = svgRef.current;
      const current = viewRef.current;
      const minScale = fitScale * MIN_SCALE_FACTOR;
      const maxScale = fitScale * MAX_SCALE_FACTOR;
      const nextScale = Math.min(maxScale, Math.max(minScale, current.scale * factor));
      if (nextScale === current.scale) return;
      let px = containerSize.width / 2;
      let py = containerSize.height / 2;
      if (svg && clientX !== undefined && clientY !== undefined) {
        const rect = svg.getBoundingClientRect();
        px = clientX - rect.left;
        py = clientY - rect.top;
      }
      const docX = current.x + px / current.scale;
      const docY = current.y + py / current.scale;
      setView({
        scale: nextScale,
        x: docX - px / nextScale,
        y: docY - py / nextScale,
      });
    },
    [containerSize.width, containerSize.height, fitScale],
  );

  useEffect(() => {
    if (handleRef) {
      handleRef.current = { fitToView, zoomBy: (factor) => zoomAt(factor) };
    }
  }, [handleRef, fitToView, zoomAt]);

  // Wheel-Zoom (nicht-passiv, damit die Seite nicht scrollt).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      zoomAt(factor, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  const toDoc = useCallback((clientX: number, clientY: number): PlanPoint => {
    const svg = svgRef.current;
    const v = viewRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: v.x + (clientX - rect.left) / v.scale,
      y: v.y + (clientY - rect.top) / v.scale,
    };
  }, []);

  const handlePx = HANDLE_PX / view.scale;
  const nonScaling = { vectorEffect: "non-scaling-stroke" as const };

  // --- Dokument-Mutationen ------------------------------------------------------------
  const updateShape = useCallback(
    (id: string, patch: (shape: PlanShape) => PlanShape, commit: boolean) => {
      const current = docRef.current;
      const next = { ...current, shapes: current.shapes.map((s) => (s.id === id ? patch(s) : s)) };
      docRef.current = next;
      onChange(next, commit);
    },
    [onChange],
  );

  const updateSymbol = useCallback(
    (id: string, patch: (symbol: PlanSymbol) => PlanSymbol, commit: boolean) => {
      const current = docRef.current;
      const next = { ...current, symbols: current.symbols.map((s) => (s.id === id ? patch(s) : s)) };
      docRef.current = next;
      onChange(next, commit);
    },
    [onChange],
  );

  const updateText = useCallback(
    (id: string, patch: (text: PlanText) => PlanText, commit: boolean) => {
      const current = docRef.current;
      const next = { ...current, texts: current.texts.map((t) => (t.id === id ? patch(t) : t)) };
      docRef.current = next;
      onChange(next, commit);
    },
    [onChange],
  );

  const updateRoute = useCallback(
    (id: string, patch: (route: PlanRoute) => PlanRoute, commit: boolean) => {
      const current = docRef.current;
      const next = { ...current, routes: (current.routes ?? []).map((r) => (r.id === id ? patch(r) : r)) };
      docRef.current = next;
      onChange(next, commit);
    },
    [onChange],
  );

  const deleteSelection = useCallback(() => {
    if (!selection || !canEdit) return;
    const current = docRef.current;
    let next: SitePlanVectorDocument;
    if (selection.kind === "shape") {
      const target = current.shapes.find((s) => s.id === selection.id);
      if (target?.locked) return;
      next = { ...current, shapes: current.shapes.filter((s) => s.id !== selection.id) };
    } else if (selection.kind === "symbol") {
      next = { ...current, symbols: current.symbols.filter((s) => s.id !== selection.id) };
    } else if (selection.kind === "route") {
      next = { ...current, routes: (current.routes ?? []).filter((r) => r.id !== selection.id) };
    } else {
      next = { ...current, texts: current.texts.filter((t) => t.id !== selection.id) };
    }
    docRef.current = next;
    onChange(next, true);
    onSelectionChange(null);
  }, [selection, canEdit, onChange, onSelectionChange]);

  const nudgeSelection = useCallback(
    (dx: number, dy: number) => {
      if (!selection || !canEdit) return;
      if (selection.kind === "shape") {
        updateShape(
          selection.id,
          (s) => (s.locked ? s : { ...s, points: translatePoints(s.points, dx, dy).map((p) => clampPoint(p, docRef.current)) }),
          true,
        );
      } else if (selection.kind === "symbol") {
        updateSymbol(selection.id, (s) => ({ ...s, ...clampPoint({ x: s.x + dx, y: s.y + dy }, docRef.current) }), true);
      } else if (selection.kind === "route") {
        updateRoute(
          selection.id,
          (r) => ({ ...r, points: translatePoints(r.points, dx, dy).map((p) => clampPoint(p, docRef.current)) }),
          true,
        );
      } else {
        updateText(selection.id, (t) => ({ ...t, ...clampPoint({ x: t.x + dx, y: t.y + dy }, docRef.current) }), true);
      }
    },
    [selection, canEdit, updateShape, updateSymbol, updateText, updateRoute],
  );

  const finishPolygon = useCallback(() => {
    if (draftPoints.length < 3) {
      setDraftPoints([]);
      return;
    }
    const shape = createShape(
      draftPoints.map((p) => roundPoint(clampPoint(p, docRef.current))),
      { layer: newShapeLayer, fill: layerDefaultFill(newShapeLayer) },
    );
    const next = { ...docRef.current, shapes: [...docRef.current.shapes, shape] };
    docRef.current = next;
    onChange(next, true);
    setDraftPoints([]);
    onSelectionChange({ kind: "shape", id: shape.id });
    onToolChange({ kind: "select" });
  }, [draftPoints, newShapeLayer, onChange, onSelectionChange, onToolChange]);

  const finishRoute = useCallback(() => {
    if (tool.kind !== "route" || draftPoints.length < 2) {
      setDraftPoints([]);
      return;
    }
    const route = createRoute(
      draftPoints.map((p) => roundPoint(clampPoint(p, docRef.current))),
      { pitch_id: tool.pitchId },
    );
    const next = { ...docRef.current, routes: [...(docRef.current.routes ?? []), route] };
    docRef.current = next;
    onChange(next, true);
    setDraftPoints([]);
    onSelectionChange({ kind: "route", id: route.id });
    onToolChange({ kind: "select" });
    onRouteFinished?.(route);
  }, [tool, draftPoints, onChange, onSelectionChange, onToolChange, onRouteFinished]);

  const cancelDraft = useCallback(() => {
    setDraftPoints([]);
    setRectDraft(null);
    gestureRef.current = null;
  }, []);

  // Werkzeugwechsel bricht laufende Zeichnungen ab.
  useEffect(() => {
    setDraftPoints([]);
    setRectDraft(null);
  }, [tool.kind]);

  // --- Tastatur -----------------------------------------------------------------------
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === " " && !e.repeat) {
        setSpaceHeld(true);
        e.preventDefault();
        return;
      }
      if (e.key === "Escape") {
        if (draftPoints.length > 0 || rectDraft) {
          cancelDraft();
        } else if (tool.kind !== "select") {
          onToolChange({ kind: "select" });
        } else {
          onSelectionChange(null);
        }
        e.preventDefault();
        return;
      }
      if (e.key === "Enter" && tool.kind === "polygon") {
        finishPolygon();
        e.preventDefault();
        return;
      }
      if (e.key === "Enter" && tool.kind === "route") {
        finishRoute();
        e.preventDefault();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selection) {
        const target = e.target as HTMLElement;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
        deleteSelection();
        e.preventDefault();
        return;
      }
      const step = e.shiftKey ? 10 : 1;
      if (e.key === "ArrowLeft") nudgeSelection(-step, 0);
      else if (e.key === "ArrowRight") nudgeSelection(step, 0);
      else if (e.key === "ArrowUp") nudgeSelection(0, -step);
      else if (e.key === "ArrowDown") nudgeSelection(0, step);
      else return;
      e.preventDefault();
    },
    [
      draftPoints.length,
      rectDraft,
      cancelDraft,
      tool.kind,
      onToolChange,
      onSelectionChange,
      finishPolygon,
      finishRoute,
      selection,
      deleteSelection,
      nudgeSelection,
    ],
  );

  const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (e.key === " ") setSpaceHeld(false);
  }, []);

  // --- Pointer-Gesten -----------------------------------------------------------------
  const startPan = useCallback((e: React.PointerEvent) => {
    gestureRef.current = {
      kind: "pan",
      startClient: { x: e.clientX, y: e.clientY },
      startView: viewRef.current,
    };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }, []);

  const handleBackgroundPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (e.button === 1 || tool.kind === "pan" || spaceHeld || readOnly) {
        if (e.button === 0 || e.button === 1) {
          e.preventDefault();
          startPan(e);
        }
        return;
      }
      if (e.button !== 0) return;
      const point = toDoc(e.clientX, e.clientY);
      switch (tool.kind) {
        case "select":
          onSelectionChange(null);
          startPan(e);
          return;
        case "polygon": {
          const first = draftPoints[0];
          if (first && draftPoints.length >= 3) {
            const dist = Math.hypot(first.x - point.x, first.y - point.y) * viewRef.current.scale;
            if (dist <= CLOSE_RADIUS_PX) {
              finishPolygon();
              return;
            }
          }
          setDraftPoints((pts) => [...pts, clampPoint(point, docRef.current)]);
          return;
        }
        case "route": {
          setDraftPoints((pts) => [...pts, clampPoint(point, docRef.current)]);
          return;
        }
        case "rect": {
          const start = clampPoint(point, docRef.current);
          gestureRef.current = { kind: "rect-draw", start, current: start };
          setRectDraft({ start, current: start });
          e.currentTarget.setPointerCapture(e.pointerId);
          return;
        }
        case "symbol": {
          const symbol = createSymbol(tool.type, 0, 0, defaultSymbolSize(docRef.current));
          const clamped = roundPoint(clampPoint(point, docRef.current));
          symbol.x = clamped.x;
          symbol.y = clamped.y;
          const next = { ...docRef.current, symbols: [...docRef.current.symbols, symbol] };
          docRef.current = next;
          onChange(next, true);
          onSelectionChange({ kind: "symbol", id: symbol.id });
          return;
        }
        case "text": {
          const clamped = roundPoint(clampPoint(point, docRef.current));
          const text = createText("Text", clamped.x, clamped.y, defaultTextSize(docRef.current));
          const next = { ...docRef.current, texts: [...docRef.current.texts, text] };
          docRef.current = next;
          onChange(next, true);
          onSelectionChange({ kind: "text", id: text.id });
          onToolChange({ kind: "select" });
          return;
        }
      }
    },
    [
      tool,
      spaceHeld,
      readOnly,
      startPan,
      toDoc,
      onSelectionChange,
      draftPoints,
      finishPolygon,
      onChange,
      onToolChange,
    ],
  );

  const beginSelect = useCallback(
    (e: React.PointerEvent) => {
      if (tool.kind === "pan" || spaceHeld || e.button === 1) {
        startPan(e);
        return;
      }
      if (tool.kind !== "select" || e.button !== 0) return;
      e.stopPropagation();
      const point = toDoc(e.clientX, e.clientY);
      const hits = stackAt(docRef.current, point, pitchCodes, revealedPitchId, 10 / viewRef.current.scale);
      if (hits.length === 0) return;

      // Alt+Klick springt sofort eine Ebene tiefer, ohne zu ziehen.
      if (e.altKey) {
        onSelectionChange(nextInStack(hits, selection));
        return;
      }

      const top = hits[0]!;
      const selectedInStack = selection ? hits.find((hit) => sameHit(selection, hit)) : undefined;
      const buried = Boolean(selectedInStack && !sameHit(selection, top));
      const target = buried && selectedInStack ? selectedInStack : top;
      const already = sameHit(selection, target);
      const cycleOnClick = already && hits.length > 1;
      if (!canEdit) {
        onSelectionChange(cycleOnClick ? nextInStack(hits, target) : target);
        return;
      }
      onSelectionChange(target);

      const current = docRef.current;
      if (target.kind === "shape") {
        const shape = current.shapes.find((s) => s.id === target.id);
        if (!shape) return;
        if (shape.locked) {
          if (cycleOnClick) onSelectionChange(nextInStack(hits, target));
          return;
        }
        gestureRef.current = {
          kind: "move-shape",
          id: shape.id,
          start: point,
          original: shape.points,
          moved: false,
          cycleOnClick,
          hitPoint: point,
        };
      } else if (target.kind === "symbol") {
        const symbol = current.symbols.find((s) => s.id === target.id);
        if (!symbol) return;
        gestureRef.current = {
          kind: "move-symbol",
          id: symbol.id,
          start: point,
          originalX: symbol.x,
          originalY: symbol.y,
          moved: false,
          cycleOnClick,
          hitPoint: point,
        };
      } else if (target.kind === "route") {
        const route = (current.routes ?? []).find((r) => r.id === target.id);
        if (!route) return;
        gestureRef.current = {
          kind: "move-route",
          id: route.id,
          start: point,
          original: route.points,
          moved: false,
          cycleOnClick,
          hitPoint: point,
        };
      } else {
        const text = current.texts.find((t) => t.id === target.id);
        if (!text) return;
        gestureRef.current = {
          kind: "move-text",
          id: text.id,
          start: point,
          originalX: text.x,
          originalY: text.y,
          moved: false,
          cycleOnClick,
          hitPoint: point,
        };
      }
      svgRef.current?.setPointerCapture(e.pointerId);
    },
    [tool.kind, spaceHeld, startPan, toDoc, pitchCodes, revealedPitchId, selection, onSelectionChange, canEdit],
  );

  const handleVertexPointerDown = useCallback(
    (e: React.PointerEvent, shapeId: string, index: number) => {
      if (!canEdit || e.button !== 0) return;
      e.stopPropagation();
      if (e.altKey) {
        // Alt+Klick entfernt den Eckpunkt
        updateShape(
          shapeId,
          (s) => (s.points.length > 3 ? { ...s, points: s.points.filter((_, i) => i !== index) } : s),
          true,
        );
        return;
      }
      gestureRef.current = { kind: "vertex", id: shapeId, index, moved: false };
      svgRef.current?.setPointerCapture(e.pointerId);
    },
    [canEdit, updateShape],
  );

  const handleMidpointPointerDown = useCallback(
    (e: React.PointerEvent, shapeId: string, afterIndex: number, point: PlanPoint) => {
      if (!canEdit || e.button !== 0) return;
      e.stopPropagation();
      updateShape(
        shapeId,
        (s) => {
          const points = [...s.points];
          points.splice(afterIndex + 1, 0, roundPoint(point));
          return { ...s, points };
        },
        false,
      );
      gestureRef.current = { kind: "vertex", id: shapeId, index: afterIndex + 1, moved: true };
      svgRef.current?.setPointerCapture(e.pointerId);
    },
    [canEdit, updateShape],
  );

  const removeRouteVertex = useCallback(
    (routeId: string, index: number) => {
      updateRoute(
        routeId,
        (r) => (r.points.length > 2 ? { ...r, points: r.points.filter((_, i) => i !== index) } : r),
        true,
      );
    },
    [updateRoute],
  );

  const handleRouteVertexPointerDown = useCallback(
    (e: React.PointerEvent, routeId: string, index: number) => {
      if (!canEdit || e.button !== 0) return;
      e.stopPropagation();
      if (e.altKey) {
        removeRouteVertex(routeId, index);
        return;
      }
      gestureRef.current = { kind: "route-vertex", id: routeId, index, moved: false };
      svgRef.current?.setPointerCapture(e.pointerId);
    },
    [canEdit, removeRouteVertex],
  );

  const handleRouteVertexContextMenu = useCallback(
    (e: React.MouseEvent, routeId: string, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (!canEdit) return;
      removeRouteVertex(routeId, index);
    },
    [canEdit, removeRouteVertex],
  );

  const handleRouteMidpointPointerDown = useCallback(
    (e: React.PointerEvent, routeId: string, afterIndex: number, point: PlanPoint) => {
      if (!canEdit || e.button !== 0) return;
      e.stopPropagation();
      updateRoute(
        routeId,
        (r) => {
          const points = [...r.points];
          points.splice(afterIndex + 1, 0, roundPoint(point));
          return { ...r, points };
        },
        false,
      );
      gestureRef.current = { kind: "route-vertex", id: routeId, index: afterIndex + 1, moved: true };
      svgRef.current?.setPointerCapture(e.pointerId);
    },
    [canEdit, updateRoute],
  );

  const handleResizeHandlePointerDown = useCallback(
    (e: React.PointerEvent, symbol: PlanSymbol) => {
      if (!canEdit || e.button !== 0) return;
      e.stopPropagation();
      gestureRef.current = { kind: "resize-symbol", id: symbol.id, center: { x: symbol.x, y: symbol.y }, moved: false };
      svgRef.current?.setPointerCapture(e.pointerId);
    },
    [canEdit],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const point = toDoc(e.clientX, e.clientY);
      if (tool.kind === "polygon" || tool.kind === "rect" || tool.kind === "route") setCursorPoint(point);
      const gesture = gestureRef.current;
      if (!gesture) return;
      switch (gesture.kind) {
        case "pan": {
          const dx = (e.clientX - gesture.startClient.x) / gesture.startView.scale;
          const dy = (e.clientY - gesture.startClient.y) / gesture.startView.scale;
          setView({ scale: gesture.startView.scale, x: gesture.startView.x - dx, y: gesture.startView.y - dy });
          return;
        }
        case "move-shape": {
          const dx = point.x - gesture.start.x;
          const dy = point.y - gesture.start.y;
          if (!gesture.moved && Math.hypot(dx, dy) * viewRef.current.scale < 2) return;
          gesture.moved = true;
          updateShape(
            gesture.id,
            (s) => ({ ...s, points: translatePoints(gesture.original, dx, dy).map((p) => roundPoint(clampPoint(p, docRef.current))) }),
            false,
          );
          return;
        }
        case "move-symbol": {
          const dx = point.x - gesture.start.x;
          const dy = point.y - gesture.start.y;
          if (!gesture.moved && Math.hypot(dx, dy) * viewRef.current.scale < 2) return;
          gesture.moved = true;
          const next = roundPoint(clampPoint({ x: gesture.originalX + dx, y: gesture.originalY + dy }, docRef.current));
          updateSymbol(gesture.id, (s) => ({ ...s, x: next.x, y: next.y }), false);
          return;
        }
        case "move-text": {
          const dx = point.x - gesture.start.x;
          const dy = point.y - gesture.start.y;
          if (!gesture.moved && Math.hypot(dx, dy) * viewRef.current.scale < 2) return;
          gesture.moved = true;
          const next = roundPoint(clampPoint({ x: gesture.originalX + dx, y: gesture.originalY + dy }, docRef.current));
          updateText(gesture.id, (t) => ({ ...t, x: next.x, y: next.y }), false);
          return;
        }
        case "move-route": {
          const dx = point.x - gesture.start.x;
          const dy = point.y - gesture.start.y;
          if (!gesture.moved && Math.hypot(dx, dy) * viewRef.current.scale < 3) return;
          gesture.moved = true;
          updateRoute(
            gesture.id,
            (r) => ({
              ...r,
              points: translatePoints(gesture.original, dx, dy).map((p) => roundPoint(clampPoint(p, docRef.current))),
            }),
            false,
          );
          return;
        }
        case "route-vertex": {
          gesture.moved = true;
          const next = roundPoint(clampPoint(point, docRef.current));
          updateRoute(
            gesture.id,
            (r) => ({ ...r, points: r.points.map((p, i) => (i === gesture.index ? next : p)) }),
            false,
          );
          return;
        }
        case "vertex": {
          gesture.moved = true;
          const next = roundPoint(clampPoint(point, docRef.current));
          updateShape(
            gesture.id,
            (s) => ({ ...s, points: s.points.map((p, i) => (i === gesture.index ? next : p)) }),
            false,
          );
          return;
        }
        case "rect-draw": {
          gesture.current = clampPoint(point, docRef.current);
          setRectDraft({ start: gesture.start, current: gesture.current });
          return;
        }
        case "resize-symbol": {
          gesture.moved = true;
          const dist = Math.hypot(point.x - gesture.center.x, point.y - gesture.center.y);
          const size = Math.max(4, Math.round(dist * 2 * Math.SQRT1_2));
          updateSymbol(gesture.id, (s) => ({ ...s, size }), false);
          return;
        }
      }
    },
    [toDoc, tool.kind, updateShape, updateSymbol, updateText, updateRoute],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const gesture = gestureRef.current;
      gestureRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Pointer war nicht gefangen
      }
      if (!gesture) return;
      switch (gesture.kind) {
        case "move-shape":
        case "move-symbol":
        case "move-text":
        case "move-route":
          if (gesture.moved) {
            onChange(docRef.current, true);
            return;
          }
          if (gesture.cycleOnClick) {
            const hits = stackAt(docRef.current, gesture.hitPoint, pitchCodes, revealedPitchId, 10 / viewRef.current.scale);
            const current: EditorSelection =
              gesture.kind === "move-shape"
                ? { kind: "shape", id: gesture.id }
                : gesture.kind === "move-symbol"
                  ? { kind: "symbol", id: gesture.id }
                  : gesture.kind === "move-route"
                    ? { kind: "route", id: gesture.id }
                    : { kind: "text", id: gesture.id };
            onSelectionChange(nextInStack(hits, current));
          }
          return;
        case "vertex":
        case "route-vertex":
        case "resize-symbol":
          if (gesture.moved) onChange(docRef.current, true);
          return;
        case "rect-draw": {
          setRectDraft(null);
          const { start, current } = gesture;
          const w = Math.abs(current.x - start.x);
          const h = Math.abs(current.y - start.y);
          if (w * viewRef.current.scale < 4 || h * viewRef.current.scale < 4) return;
          const x0 = Math.min(start.x, current.x);
          const y0 = Math.min(start.y, current.y);
          const shape = createShape(
            [
              { x: x0, y: y0 },
              { x: x0 + w, y: y0 },
              { x: x0 + w, y: y0 + h },
              { x: x0, y: y0 + h },
            ].map((p) => roundPoint(p)),
            { layer: newShapeLayer, fill: layerDefaultFill(newShapeLayer) },
          );
          const next = { ...docRef.current, shapes: [...docRef.current.shapes, shape] };
          docRef.current = next;
          onChange(next, true);
          onSelectionChange({ kind: "shape", id: shape.id });
          onToolChange({ kind: "select" });
          return;
        }
        default:
          return;
      }
    },
    [onChange, onSelectionChange, onToolChange, newShapeLayer, pitchCodes, revealedPitchId],
  );

  const handleDoubleClick = useCallback(() => {
    if (tool.kind === "polygon" && draftPoints.length >= 3) finishPolygon();
    if (tool.kind === "route" && draftPoints.length >= 2) finishRoute();
  }, [tool.kind, draftPoints.length, finishPolygon, finishRoute]);

  // --- Rendering ------------------------------------------------------------------------
  const sortedShapes = useMemo(
    () => [...doc.shapes].sort((a, b) => layerOrder(a.layer) - layerOrder(b.layer)),
    [doc.shapes],
  );

  const selectedShape = selection?.kind === "shape" ? doc.shapes.find((s) => s.id === selection.id) ?? null : null;
  const selectedSymbol = selection?.kind === "symbol" ? doc.symbols.find((s) => s.id === selection.id) ?? null : null;
  const selectedText = selection?.kind === "text" ? doc.texts.find((t) => t.id === selection.id) ?? null : null;
  const selectedRoute =
    selection?.kind === "route" ? (doc.routes ?? []).find((r) => r.id === selection.id) ?? null : null;
  const visibleRoutes = (doc.routes ?? []).filter((route) => isRouteVisible(route, revealedPitchId));

  const viewBox = `${view.x} ${view.y} ${containerSize.width / view.scale || doc.width} ${containerSize.height / view.scale || doc.height}`;
  const baseStroke = Math.max(0.5, Math.min(doc.width, doc.height) * 0.0015);
  const cursor = spaceHeld ? "grab" : cursorForTool(tool, readOnly);

  const draftPreview = useMemo(() => {
    if ((tool.kind !== "polygon" && tool.kind !== "route") || draftPoints.length === 0) return null;
    const points = cursorPoint ? [...draftPoints, cursorPoint] : draftPoints;
    return pointsToAttr(points);
  }, [tool.kind, draftPoints, cursorPoint]);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={clsx(
        "site-plan-editor relative select-none overflow-hidden rounded-xl border border-separator bg-fill-tertiary/40 outline-none focus-visible:ring-2 focus-visible:ring-accent",
        className,
      )}
      style={{ cursor, touchAction: "none" }}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onBlur={() => setSpaceHeld(false)}
      onPointerDownCapture={() => {
        // Tastaturkürzel (Enter/Esc/Entf/Pfeile) sollen nach jedem Klick in den Plan greifen.
        const el = containerRef.current;
        if (el && document.activeElement !== el) el.focus({ preventScroll: true });
      }}
    >
      <svg
        ref={svgRef}
        className="block h-full w-full"
        viewBox={viewBox}
        preserveAspectRatio="none"
        role="img"
        aria-label="Platzplan-Editor"
        onPointerDown={handleBackgroundPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        <SitePlanSymbolDefs />

        {/* Plan-Fläche */}
        <rect
          x={0}
          y={0}
          width={doc.width}
          height={doc.height}
          fill={doc.background_color ?? "#ffffff"}
          stroke="#9ca3af"
          strokeWidth={1}
          {...nonScaling}
        />
        {sourceImage && showSourceImage && !imageError ? (
          <image
            href={sourceImage}
            x={0}
            y={0}
            width={doc.width}
            height={doc.height}
            preserveAspectRatio="none"
            opacity={sourceImageOpacity}
            style={{ pointerEvents: "none" }}
            onError={() => setImageError(true)}
          />
        ) : null}

        {/* Flächen */}
        <g>
          {sortedShapes.map((shape) => {
            const selected = selectedShape?.id === shape.id;
            const resolved = resolvedShapeFill(shape, pitchCategoryColors) ?? layerDefaultFill(shape.layer);
            const fill = resolved === "none" ? "transparent" : resolved;
            const unassignedPitch = shape.layer === "pitch" && !shape.pitch_id;
            return (
              <polygon
                key={shape.id}
                points={pointsToAttr(shape.points)}
                fill={fill === "none" ? "transparent" : fill}
                fillOpacity={shape.opacity ?? (fill === "none" ? 0 : 0.88)}
                stroke={selected ? "var(--eh-accent)" : shape.stroke ?? "#1f2937"}
                strokeWidth={selected ? baseStroke * 2 : shape.stroke_width ?? baseStroke}
                strokeDasharray={unassignedPitch && !selected ? `${baseStroke * 4} ${baseStroke * 3}` : undefined}
                strokeLinejoin="round"
                className={clsx(tool.kind === "select" && canEdit && !shape.locked && "cursor-move")}
                style={{ pointerEvents: tool.kind === "select" || tool.kind === "pan" ? "visiblePainted" : "none" }}
                onPointerDown={beginSelect}
              />
            );
          })}
        </g>

        {/* Beschriftungen – auf die Fläche begrenzt, damit Nachbarn sich nicht überdecken */}
        {showLabels ? (
          <g
            style={{ pointerEvents: "none" }}
            fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
            fontWeight={600}
          >
            <defs>
              {doc.shapes.map((shape) => (
                <clipPath key={`clip-${shape.id}`} id={`sp-label-${shape.id}`}>
                  <polygon points={pointsToAttr(shape.points)} />
                </clipPath>
              ))}
            </defs>
            {doc.shapes.map((shape) => {
              const label = shapeDisplayLabel(shape, pitchCodes);
              if (!label) return null;
              const c = shapeCentroid(shape);
              const size = shapeLabelFontSize(shape, label);
              const textLength = shapeLabelTextLength(shape, label, size);
              return (
                <text
                  key={`label-${shape.id}`}
                  x={c.x}
                  y={c.y}
                  fontSize={size}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#111827"
                  stroke="#ffffff"
                  strokeWidth={size * 0.12}
                  paintOrder="stroke"
                  strokeLinejoin="round"
                  clipPath={`url(#sp-label-${shape.id})`}
                  textLength={textLength ?? undefined}
                  lengthAdjust={textLength !== null ? "spacingAndGlyphs" : undefined}
                >
                  {label}
                </text>
              );
            })}
          </g>
        ) : null}

        {/* Symbole */}
        <g>
          {doc.symbols.map((symbol) => {
            const def = getSymbolDefinition(symbol.type);
            const scale = symbol.size / SYMBOL_BOX;
            const selected = selectedSymbol?.id === symbol.id;
            return (
              <g
                key={symbol.id}
                transform={`translate(${symbol.x} ${symbol.y}) rotate(${symbol.rotation})`}
                className={clsx(tool.kind === "select" && canEdit && "cursor-move")}
                style={{ pointerEvents: tool.kind === "select" || tool.kind === "pan" ? "all" : "none" }}
                onPointerDown={beginSelect}
              >
                {/* unsichtbare Trefferfläche */}
                <rect
                  x={-symbol.size / 2}
                  y={-symbol.size / 2}
                  width={symbol.size}
                  height={symbol.size}
                  fill="transparent"
                />
                <use
                  href={`#${symbolDomId(symbol.type)}`}
                  width={SYMBOL_BOX}
                  height={SYMBOL_BOX}
                  transform={`scale(${scale}) translate(${-SYMBOL_BOX / 2} ${-SYMBOL_BOX / 2})`}
                  color={symbol.color ?? def.color}
                  style={{ pointerEvents: "none" }}
                />
                {selected ? (
                  <rect
                    x={-symbol.size / 2}
                    y={-symbol.size / 2}
                    width={symbol.size}
                    height={symbol.size}
                    fill="none"
                    stroke="var(--eh-accent)"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    {...nonScaling}
                    style={{ pointerEvents: "none" }}
                  />
                ) : null}
              </g>
            );
          })}
        </g>

        {/* Freie Texte */}
        <g fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif" fontWeight={600}>
          {doc.texts.map((text) => {
            if (isDuplicateLabelText(text, doc, pitchCodes)) return null;
            const selected = selectedText?.id === text.id;
            return (
              <text
                key={text.id}
                x={text.x}
                y={text.y}
                fontSize={text.size}
                textAnchor="middle"
                dominantBaseline="central"
                fill={text.color ?? "#111827"}
                transform={text.rotation ? `rotate(${text.rotation} ${text.x} ${text.y})` : undefined}
                className={clsx(tool.kind === "select" && canEdit && "cursor-move")}
                style={{
                  pointerEvents: tool.kind === "select" || tool.kind === "pan" ? "all" : "none",
                  textDecoration: selected ? "underline" : undefined,
                }}
                onPointerDown={beginSelect}
              >
                {text.text}
              </text>
            );
          })}
        </g>

        {/* Anfahrten: nur ohne Platz oder solange der Platz eingeblendet ist */}
        <g>
          {visibleRoutes.map((route) => {
            const selected = selectedRoute?.id === route.id;
            const color = route.color ?? ROUTE_COLOR;
            return (
              <polyline
                key={route.id}
                points={pointsToAttr(route.points)}
                fill="none"
                stroke={selected ? "var(--eh-accent)" : color}
                strokeWidth={route.stroke_width ?? 3}
                strokeLinejoin="round"
                strokeLinecap="round"
                {...nonScaling}
                style={{ pointerEvents: tool.kind === "select" || tool.kind === "pan" ? "stroke" : "none" }}
                onPointerDown={beginSelect}
              />
            );
          })}
          {visibleRoutes.map((route) => {
            const end = route.points[route.points.length - 1];
            if (!end) return null;
            return (
              <circle
                key={`${route.id}-end`}
                cx={end.x}
                cy={end.y}
                r={5 / view.scale}
                fill={route.color ?? ROUTE_COLOR}
                style={{ pointerEvents: "none" }}
              />
            );
          })}
        </g>

        {/* Auswahl bleibt sichtbar, auch wenn eine andere Fläche darüber liegt */}
        {selectedShape && tool.kind === "select" ? (
          <polygon
            points={pointsToAttr(selectedShape.points)}
            fill="none"
            stroke="var(--eh-accent)"
            strokeWidth={baseStroke * 2.5}
            strokeLinejoin="round"
            style={{ pointerEvents: "none" }}
          />
        ) : null}

        {/* Auswahl: Eckpunkte und Mittelpunkte */}
        {selectedShape && canEdit && !selectedShape.locked && tool.kind === "select" ? (
          <g>
            {selectedShape.points.map((p, i) => {
              const next = selectedShape.points[(i + 1) % selectedShape.points.length]!;
              const mid = { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2 };
              return (
                <circle
                  key={`mid-${i}`}
                  cx={mid.x}
                  cy={mid.y}
                  r={handlePx * 0.65}
                  fill="#ffffff"
                  fillOpacity={0.9}
                  stroke="var(--eh-accent)"
                  strokeWidth={1}
                  {...nonScaling}
                  className="cursor-copy"
                  onPointerDown={(e) => handleMidpointPointerDown(e, selectedShape.id, i, mid)}
                />
              );
            })}
            {selectedShape.points.map((p, i) => (
              <circle
                key={`v-${i}`}
                cx={p.x}
                cy={p.y}
                r={handlePx}
                fill="var(--eh-accent)"
                stroke="#ffffff"
                strokeWidth={2}
                {...nonScaling}
                className="cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => handleVertexPointerDown(e, selectedShape.id, i)}
              >
                <title>Ziehen zum Verschieben · Alt+Klick entfernt</title>
              </circle>
            ))}
          </g>
        ) : null}

        {selectedRoute && canEdit && tool.kind === "select" && isRouteVisible(selectedRoute, revealedPitchId) ? (
          <g>
            {selectedRoute.points.slice(0, -1).map((p, i) => {
              const next = selectedRoute.points[i + 1]!;
              const mid = { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2 };
              return (
                <circle
                  key={`rmid-${i}`}
                  cx={mid.x}
                  cy={mid.y}
                  r={handlePx * 0.65}
                  fill="#ffffff"
                  stroke={ROUTE_COLOR}
                  strokeWidth={1}
                  {...nonScaling}
                  className="cursor-copy"
                  onPointerDown={(e) => handleRouteMidpointPointerDown(e, selectedRoute.id, i, mid)}
                />
              );
            })}
            {selectedRoute.points.map((p, i) => (
              <circle
                key={`rv-${i}`}
                cx={p.x}
                cy={p.y}
                r={handlePx}
                fill={i === selectedRoute.points.length - 1 ? ROUTE_COLOR : "#ffffff"}
                stroke={ROUTE_COLOR}
                strokeWidth={2}
                {...nonScaling}
                className="cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => handleRouteVertexPointerDown(e, selectedRoute.id, i)}
                onContextMenu={(e) => handleRouteVertexContextMenu(e, selectedRoute.id, i)}
              >
                <title>Ziehen · Rechtsklick entfernt</title>
              </circle>
            ))}
          </g>
        ) : null}

        {/* Symbol-Größe per Griff */}
        {selectedSymbol && canEdit && tool.kind === "select" ? (
          <circle
            cx={selectedSymbol.x + selectedSymbol.size / 2}
            cy={selectedSymbol.y + selectedSymbol.size / 2}
            r={handlePx}
            fill="#ffffff"
            stroke="var(--eh-accent)"
            strokeWidth={2}
            {...nonScaling}
            className="cursor-nwse-resize"
            onPointerDown={(e) => handleResizeHandlePointerDown(e, selectedSymbol)}
          >
            <title>Größe ändern</title>
          </circle>
        ) : null}

        {/* Zeichnen: Polygon-Entwurf */}
        {draftPreview ? (
          <g style={{ pointerEvents: "none" }}>
            {tool.kind === "route" ? (
              <polyline
                points={draftPreview}
                fill="none"
                stroke={ROUTE_COLOR}
                strokeWidth={3}
                strokeLinejoin="round"
                strokeLinecap="round"
                {...nonScaling}
              />
            ) : (
              <polygon
                points={draftPreview}
                fill="var(--eh-accent)"
                fillOpacity={0.15}
                stroke="var(--eh-accent)"
                strokeWidth={1.5}
                strokeDasharray="5 4"
                {...nonScaling}
              />
            )}
            {draftPoints.map((p, i) => (
              <circle
                key={`d-${i}`}
                cx={p.x}
                cy={p.y}
                r={i === 0 && draftPoints.length >= 3 ? handlePx * 1.3 : handlePx * 0.8}
                fill={i === 0 ? "#ffffff" : "var(--eh-accent)"}
                stroke="var(--eh-accent)"
                strokeWidth={2}
                {...nonScaling}
              />
            ))}
          </g>
        ) : null}

        {/* Zeichnen: Rechteck-Entwurf */}
        {rectDraft ? (
          <rect
            x={Math.min(rectDraft.start.x, rectDraft.current.x)}
            y={Math.min(rectDraft.start.y, rectDraft.current.y)}
            width={Math.abs(rectDraft.current.x - rectDraft.start.x)}
            height={Math.abs(rectDraft.current.y - rectDraft.start.y)}
            fill="var(--eh-accent)"
            fillOpacity={0.15}
            stroke="var(--eh-accent)"
            strokeWidth={1.5}
            strokeDasharray="5 4"
            {...nonScaling}
            style={{ pointerEvents: "none" }}
          />
        ) : null}
      </svg>

      {/* Hinweise */}
      <div className="pointer-events-none absolute bottom-2 left-2 right-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-label-secondary">
        <span className="rounded bg-card/85 px-2 py-0.5 shadow-sm">
          {tool.kind === "polygon"
            ? draftPoints.length === 0
              ? "Klicken Sie Eckpunkte entlang der Fläche. Doppelklick, Enter oder Klick auf den Startpunkt schließt."
              : `${draftPoints.length} Punkt(e) – Doppelklick/Enter schließt, Esc bricht ab.`
            : tool.kind === "rect"
              ? "Rechteck aufziehen."
              : tool.kind === "route"
                ? draftPoints.length === 0
                  ? "Anfahrt zeichnen: Eckpunkte setzen. Doppelklick oder Enter beendet die Linie."
                  : `${draftPoints.length} Punkt(e) – Doppelklick oder Enter beendet, Esc bricht ab.`
                : tool.kind === "symbol"
                ? `„${getSymbolDefinition(tool.type).label}“ platzieren – Klick auf den Plan, Esc beendet.`
                : tool.kind === "text"
                  ? "Klicken Sie, wo der Text stehen soll."
                  : tool.kind === "pan"
                    ? "Ziehen verschiebt die Ansicht."
                    : "Klick wählt das oberste Objekt, erneuter Klick oder Alt+Klick das darunter · Ziehen verschiebt die Auswahl · Entf löscht"}
        </span>
        <span className="rounded bg-card/85 px-2 py-0.5 shadow-sm">{Math.round((view.scale / fitScale) * 100)} %</span>
      </div>
    </div>
  );
}
